import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Box,
  Button,
  DataTableToolbar,
  DataTableToolbarActions,
  Icon,
  Separator,
  useIsMobile,
} from "@salestrak/ui";
import { ViewSearch } from "#app/components/filters/view-search";
import { PageHeader } from "#app/components/page-header";
import {
  WORKFLOWS_DEFAULT_HIDDEN_COLUMN_IDS,
  WorkflowsTable,
  useWorkflowColumns,
} from "#app/components/workflows/workflows-table";
import { CreateWorkflowDialog } from "#app/components/workflows/create-workflow-dialog";
import { DeleteWorkflowDialog } from "#app/components/workflows/delete-workflow-dialog";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { WORKFLOW_LIST } from "#app/lib/filters/list-configs";
import { useAppData, type WorkflowRow } from "#app/lib/data";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";
import { withMemberModule } from "#app/components/permissions/require-permission";

/**
 * `/workflows` — the workflows roster: a plain `DataTable` of every workflow (a
 * single workflow's issues live at `/workflow/$workflowId`). Composed on the
 * teams / dashboards roster grammar: the title row carries the lone create
 * action, the toolbar band under it carries search, and column show/hide is
 * the circle-plus picker on the actions-column header.
 */
function WorkflowsRoute() {
  const { push } = useAppNavigate();
  const { refetch: refetchAppData } = useAppData();
  const isMobile = useIsMobile();
  const canCreate = usePermission("create.workflows");

  const { searchValue, setSearchValue, sorting, setSorting, where, orderBy } =
    useListQueryState(WORKFLOW_LIST);

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteWorkflow, setDeleteWorkflow] = useState<WorkflowRow | null>(null);

  // Truly empty — zero workflows with no search narrowing the list. The
  // toolbar has nothing to operate on, so it yields to the title row's create
  // action. A zero-result search keeps it: the field is how the viewer widens
  // the view again.
  const [workflowTotal, setWorkflowTotal] = useState<number | undefined>(undefined);
  const isTrulyEmpty = workflowTotal === 0 && !searchValue;

  const onEdit = useCallback(
    (workflowId: string) => push(`/settings/workflows/${workflowId}`),
    [push],
  );

  // `/workflow/:id` has no view of its own — it redirects to the workflow's
  // default view.
  const onOpenWorkflow = useCallback(
    (workflowId: string) => push(`/workflow/${workflowId}`),
    [push],
  );

  const columns = useWorkflowColumns(onEdit, onOpenWorkflow);
  const tableView = useTableViewSettings("workflows:app", {
    defaultHiddenColumnIds: WORKFLOWS_DEFAULT_HIDDEN_COLUMN_IDS,
  });

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="Workflows"
        actions={
          canCreate ? (
            <Button
              aria-label="Create Workflow"
              size={isMobile ? "icon-xs" : "xs"}
              onClick={() => setCreateOpen(true)}
            >
              {/* Raw string: a bare Text element pins 14px into the 24px
                * rung; a string child inherits the button's own 12px font. */}
              {isMobile ? <Icon name="plus" /> : "Create Workflow"}
            </Button>
          ) : undefined
        }
      />
      <Separator />
      {!isTrulyEmpty && (
        <DataTableToolbar>
          <DataTableToolbarActions>
            <ViewSearch
              onValueChange={setSearchValue}
              size="xs"
            />
          </DataTableToolbarActions>
        </DataTableToolbar>
      )}
      <WorkflowsTable
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        columns={columns}
        tableViewProps={tableView.tableProps}
        onColumnVisibilityChange={tableView.settingsProps.onColumnVisibilityChange}
        onRequestDelete={setDeleteWorkflow}
        onTotalChange={setWorkflowTotal}
      />
      <CreateWorkflowDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        // The workflow page resolves its workflow from app data, so refresh
        // that first, then land on the new workflow's own page — only the
        // settings list lands in settings; every other entry point stays in
        // the app.
        onCreated={async (workflowId) => {
          await refetchAppData();
          push(`/workflow/${workflowId}`);
        }}
      />
      <DeleteWorkflowDialog
        workflow={deleteWorkflow}
        open={!!deleteWorkflow}
        onOpenChange={(open) => {
          if (!open) setDeleteWorkflow(null);
        }}
        onManageWorkflow={onEdit}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/workflows")({
  // A member module: a Guest never reaches it (`ModuleTier`). The rail
  // omits the tile; the route denies for itself since the URL still resolves.
  component: withMemberModule(WorkflowsRoute),
});
